import React, { useEffect, useState } from 'react';

import { useGetEvent, useGetSample } from '@vlabs/api/luna-api/client';
import type { Uuid } from '@vlabs/api/luna-api/model/Uuid';
import { ZoomInIcon, DownloadIcon } from '@vlabs/icons';
import type { BodySampleProps } from '@vlabs/uikit';
import { BodySample, ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';

import { useFullscreenImageViewer } from '@vlabs/monokit/features/view-photo';
import { downloadImage } from '@vlabs/monokit/shared/lib/images';

import { buildTestid } from '../../../../shared/lib/utils';

interface EventBodyTopMatchSampleProps extends Omit<BodySampleProps, 'src' | 'buttonOnBottom'> {
  /** для топ-матча с событием берем body-сэмпл из его детекций */
  matchedEventId: Uuid;
}

const displayName = 'EventBodyTopMatchSample';
const testid = buildTestid(displayName);

function EventBodyTopMatchSample({
  matchedEventId,
  'data-testid': dataTestId = undefined,
  ...props
}: EventBodyTopMatchSampleProps): React.JSX.Element {
  // для body сэмпл ищем по айди события из совпадения
  const { data: topMatchEvent } = useGetEvent(
    matchedEventId,
    { target: ['body_detections'] },
  );

  const sampleId = topMatchEvent?.body_detections?.find(({ sample_id }) => !!sample_id)?.sample_id;

  const { data: sample } = useGetSample('bodies', sampleId ?? '', {
    query: { enabled: !!sampleId },
  });

  const [src, setSrc] = useState<string>();

  useEffect(() => {
    if (!sample) {
      setSrc(undefined);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(sample);
    setSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [sample]);

  const fullscreenImageViewer = useFullscreenImageViewer({ url: src });

  return (
    <BodySample
      buttonOnBottom={(
        <ButtonScrimGroup size="small">
          {/* //FIXME: Заменить на ViewPhotoIconButton и DownloadImageIconButton когда ButtonScrimGroup будет поддерживать */}
          <IconButton
            disabled={!src}
            icon={<ZoomInIcon />}
            kind="secondary"
            onClick={fullscreenImageViewer}
            size="small"
            variant="flat"
            {...testid('ZoomButton', dataTestId)}
          />
          <IconButton
            disabled={!src}
            icon={<DownloadIcon />}
            kind="secondary"
            onClick={() => downloadImage(src)}
            size="small"
            variant="flat"
            {...testid('DownloadButton', dataTestId)}
          />
        </ButtonScrimGroup>
      )}
      size="large"
      src={src}
      {...testid(dataTestId)}
      {...props}
    />
  );
}

EventBodyTopMatchSample.displayName = displayName;

export { EventBodyTopMatchSample };
